import { useCallback, useEffect, useState } from "react";
import { CalendarClock, Loader2, RotateCcw, ShoppingBag } from "lucide-react";
import { Container } from "@/components/site/PageHeader";
import { SectionHeading } from "@/components/site/SectionHeading";
import { FavoriteButton } from "@/components/site/FavoriteButton";

const RK_URL = "https://api.radioking.io/widget/radio/lgtdz/track/ckoi";
const COUNT = 15;
const pad = (n) => String(n).padStart(2, "0");
const toDateInput = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const toTimeInput = (d) => `${pad(d.getHours())}:${pad(d.getMinutes())}`;
const hour = (iso) => new Date(iso).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
const day = (iso) => new Date(iso).toLocaleDateString("fr-FR", { weekday: "short", day: "numeric", month: "short" });

const usePlayedTracks = (at) => {
  const [state, setState] = useState({ tracks: [], loading: true, error: false });
  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: false }));
    try {
      const date = (at || new Date()).toISOString();
      const res = await fetch(`${RK_URL}?limit=${COUNT}&date=${encodeURIComponent(date)}`);
      const data = await res.json();
      setState({ tracks: Array.isArray(data) ? (at ? data : data.slice(0, COUNT)) : [], loading: false, error: false });
    } catch {
      setState({ tracks: [], loading: false, error: true });
    }
  }, [at]);
  useEffect(() => {
    load();
    if (at) return undefined;
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load, at]);
  return state;
};

const inputCls = "h-12 bg-transparent border border-white/20 text-white px-4 [color-scheme:dark] focus:outline-none focus:border-primary transition-colors";

const SearchForm = ({ onSearch, onReset, searching }) => {
  const now = new Date();
  const [date, setDate] = useState(toDateInput(now));
  const [time, setTime] = useState(toTimeInput(now));
  const submit = (e) => {
    e.preventDefault();
    if (date && time) onSearch(new Date(`${date}T${time}:00`));
  };
  return (
    <form onSubmit={submit} className="flex flex-wrap items-end gap-3" data-testid="played-tracks-search">
      <label className="flex flex-col gap-2 text-[11px] uppercase tracking-[0.25em] font-bold text-zinc-300">
        Date
        <input type="date" value={date} max={toDateInput(now)} onChange={(e) => setDate(e.target.value)} className={inputCls} data-testid="played-tracks-date" />
      </label>
      <label className="flex flex-col gap-2 text-[11px] uppercase tracking-[0.25em] font-bold text-zinc-300">
        Heure
        <input type="time" value={time} onChange={(e) => setTime(e.target.value)} className={inputCls} data-testid="played-tracks-time" />
      </label>
      <button type="submit" className="h-12 inline-flex items-center gap-2 bg-primary text-white font-bold uppercase text-xs tracking-[0.2em] px-6 hover:bg-white hover:text-black transition-colors" data-testid="played-tracks-search-btn">
        <CalendarClock className="w-4 h-4" /> Rechercher
      </button>
      {searching && (
        <button type="button" onClick={onReset} className="h-12 inline-flex items-center gap-2 border border-white/30 text-white font-bold uppercase text-xs tracking-[0.2em] px-5 hover:border-primary hover:text-primary transition-colors" data-testid="played-tracks-reset-btn">
          <RotateCcw className="w-4 h-4" /> En direct
        </button>
      )}
    </form>
  );
};

const TrackRow = ({ t, badge }) => (
  <li className={`flex items-center gap-4 sm:gap-6 py-4 border-b border-white/10 ${badge ? "bg-white/[0.04] -mx-3 px-3" : ""}`} data-testid="played-track">
    <div className="w-14 shrink-0 text-right">
      <p className="font-display text-lg text-white leading-none">{hour(t.started_at)}</p>
      <p className="text-[11px] text-zinc-400 mt-1 capitalize">{day(t.started_at)}</p>
    </div>
    <img src={t.cover_url || "/brand/fallback-magazine.jpg"} alt="" loading="lazy" className="w-14 h-14 object-cover shrink-0 bg-[hsl(var(--surface))]" />
    <div className="min-w-0 flex-1">
      {badge && <p className="text-[11px] uppercase tracking-[0.25em] font-bold text-primary mb-1" data-testid="played-track-badge">{badge}</p>}
      <p className="text-white font-semibold truncate">{t.title}</p>
      <p className="text-zinc-400 text-sm truncate">{t.artist}{t.album ? ` · ${t.album}` : ""}</p>
    </div>
    {t.buy_link && (
      <a href={t.buy_link} target="_blank" rel="noreferrer" aria-label={`Acheter ${t.title}`} title="Acheter"
        className="w-10 h-10 shrink-0 grid place-items-center border border-white/15 text-zinc-300 hover:bg-primary hover:border-primary hover:text-white transition-colors" data-testid="played-track-buy">
        <ShoppingBag className="w-4 h-4" />
      </a>
    )}
    <FavoriteButton testId="played-track-favorite" className="w-10 h-10"
      track={{ artist: t.artist, title: t.title, album: t.album, cover: t.cover_url, buy_link: t.buy_link, played_at: t.started_at }} />
  </li>
);

export const PlayedTracks = () => {
  const [at, setAt] = useState(null);
  const { tracks, loading, error } = usePlayedTracks(at);
  const now = Date.now();
  const target = at ? at.getTime() : null;
  const badgeFor = (t, i) => {
    const start = new Date(t.started_at).getTime();
    const end = new Date(t.end_at).getTime();
    if (target) return start <= target && target < end ? "À cette heure-là" : null;
    return i === 0 && end > now ? "En cours" : null;
  };
  return (
    <section id="titres-diffuses" className="py-24 md:py-32 border-b border-white/10 scroll-mt-20" data-testid="radio-played-tracks">
      <Container className="grid grid-cols-1 xl:grid-cols-[1fr_1.4fr] gap-12 xl:gap-20 [&>*]:min-w-0">
        <div>
          <SectionHeading top="Titres" bottom="diffusés" testId="played-tracks-title" />
          <p className="text-zinc-300 text-base sm:text-lg leading-relaxed mt-8 max-w-md">
            {at ? `Les titres diffusés autour du ${day(at.toISOString())} à ${hour(at.toISOString())}.` : "Les derniers titres passés à l'antenne, mis à jour en direct."}
          </p>
          <div className="mt-10"><SearchForm onSearch={setAt} onReset={() => setAt(null)} searching={!!at} /></div>
        </div>
        <div className="min-w-0">
          {loading && tracks.length === 0 && <p className="flex items-center gap-3 text-zinc-400" data-testid="played-tracks-loading"><Loader2 className="w-4 h-4 animate-spin" /> Chargement…</p>}
          {error && <p className="text-zinc-400" data-testid="played-tracks-error">Impossible de charger les titres pour le moment.</p>}
          {!loading && !error && tracks.length === 0 && <p className="text-zinc-400" data-testid="played-tracks-empty">Aucun titre trouvé pour ce moment.</p>}
          <ol className={`border-t border-white/10 transition-opacity ${loading ? "opacity-60" : ""}`} data-testid="played-tracks-list">
            {tracks.map((t, i) => (
              <TrackRow key={`${t.started_at}-${i}`} t={t} badge={badgeFor(t, i)} />
            ))}
          </ol>
        </div>
      </Container>
    </section>
  );
};
